"use client";

/**
 * InsuraBotCardRenderer — Routes card data to the correct visual component.
 *
 * Priority:
 * 1. Backend-provided structured cards (message.cards)
 * 2. Frontend pattern detection on response text (fallback)
 */

import type { InsuraBotCard } from "../../types/insurabot-cards";
import TrainingProgressCard from "./TrainingProgressCard";
import QuoteResultCard from "./QuoteResultCard";
import CarrierComparisonCard from "./CarrierComparisonCard";
import ScenarioRecommendationCard from "./ScenarioRecommendationCard";
import AgentActivityCard from "./AgentActivityCard";
import TechficientQuoteFormCard from "./TechficientQuoteFormCard";
import TechficientQuoteResultCard from "./TechficientQuoteResultCard";
import CompulifeQuoteFormCard from "./CompulifeQuoteFormCard";
import EappStatusCard from "./EappStatusCard";
import EappLaunchCard from "./EappLaunchCard";
import VideoCard from "./VideoCard";

interface InsuraBotCardRendererProps {
  cards?: InsuraBotCard[];
  response?: string;
}

// Frontend pattern detection — fallback when backend doesn't send structured cards
function detectCardsFromText(response: string): InsuraBotCard[] {
  if (!response) return [];
  const detected: InsuraBotCard[] = [];

  // Detect training progress mentions
  const hasTrainingScores = /(?:training|score|dimension|objection handling|discovery|compliance|closing|rapport|pacing).*\d{2,3}\/100/i.test(response)
    || /(?:average|avg).*score.*\d{2,3}/i.test(response)
    || /(?:streak|sessions?).*\d+\s*(?:day|session)/i.test(response);

  // We can't generate structured card data from text alone for training progress
  // That requires backend metadata — so we only detect but don't render without data

  // Detect quote results (prices with carrier names)
  const hasQuotes = /\$\d+\.?\d*\s*\/?\s*mo/i.test(response)
    && /carrier|company|policy|coverage/i.test(response);

  // These also need structured data — text detection alone isn't enough for rich cards
  // The backend needs to send the cards[] array

  return detected;
}

export default function InsuraBotCardRenderer({ cards, response }: InsuraBotCardRendererProps) {
  // Use backend-provided cards if available
  const cardsToRender = cards && cards.length > 0 ? cards : detectCardsFromText(response || "");

  if (cardsToRender.length === 0) return null;

  return (
    <div className="space-y-2">
      {cardsToRender.map((card, i) => {
        switch (card.type) {
          case "training_progress":
            return <TrainingProgressCard key={i} data={card.data as any} />;
          case "quote_result":
            return <QuoteResultCard key={i} data={card.data as any} />;
          case "carrier_comparison":
            return <CarrierComparisonCard key={i} data={card.data as any} />;
          case "scenario_recommendation":
            return <ScenarioRecommendationCard key={i} data={card.data as any} />;
          case "agent_activity":
            return <AgentActivityCard key={i} data={card.data as any} />;
          case "techficient_quote_form":
            return <TechficientQuoteFormCard key={i} data={card.data as any} />;
          case "techficient_quote_result":
            return <TechficientQuoteResultCard key={i} data={card.data as any} />;
          case "compulife_quote_form":
            return <CompulifeQuoteFormCard key={i} data={card.data as any} />;
          case "eapp_status":
            return <EappStatusCard key={i} data={card.data as any} />;
          case "eapp_launch":
            return <EappLaunchCard key={i} data={card.data as any} />;
          case "video":
            return <VideoCard key={i} data={card.data as any} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
